import { useRef } from 'react'

import HeyNicTopNav from './HeyNicTopNav'
import ChatHistorySidebar from './ChatHistorySidebar'
import HeyNic from './HeyNic'
import useKeyboardInset from '../../components/useKeyboardInset'
import { CHAT_MOBILE_VIEWPORT, FocusedShell, FocusedBody, FocusedContent } from './styled'

/*
 * Focused layout for the standalone /hey-nic route. The feature-flag +
 * AI-availability guard lives in HeyNicGate (which wraps this route), so here we
 * only render the self-contained chat shell (top nav + history sidebar + chat
 * content).
 */
const HeyNicChat = () => {
  const { pinToViewport, viewportHeight, viewportOffsetTop } =
    useKeyboardInset(CHAT_MOBILE_VIEWPORT)

  // Owned here rather than in HeyNic because the mobile Recents sheet needs it
  // too, and that lives under the top nav — a sibling of the chat content. See
  // MobileChatSheet for why its tap handlers focus the input themselves.
  const inputRef = useRef(null)

  /*
   * Mobile keyboard: pin the shell to the visible area rather than the full
   * `100dvh` the keyboard doesn't shrink, so the transcript and the input footer
   * stay above it instead of being scrolled out of view.
   *
   * Fixed rather than in-flow, and that matters: in flow, the browser scrolls the
   * document to reveal the focused input, which drags the shell's bottom edge off
   * the top of the keyboard — a gap under the input, and a footer that jumps (or
   * briefly paints a ghost second input) as the scroll and the resize race each
   * other. Pinned, the body has no in-flow content left, so there is nothing for
   * the browser to scroll and the bottom edge stays put. `top` follows
   * `offsetTop` for browsers that pan the visual viewport instead of scrolling —
   * set as `top` rather than a transform on purpose, since a transform would make
   * the shell the containing block for the mobile Recents overlay (MobileChatSheet
   * is `position: fixed; inset: 0`).
   *
   * Driven by `pinToViewport`, which trusts a focused input on a mobile viewport as
   * well as a measured inset — see the hook. A cold entry autofocuses before the
   * keyboard's size can be learned, and going unpinned there is what left the input
   * cut off behind the keyboard.
   */
  const shellStyle = {
    ...(pinToViewport && {
      position: 'fixed',
      top: `${viewportOffsetTop}px`,
      left: 0,
      right: 0,
      /*
       * min() so the shell is never taller than EITHER estimate of the visible
       * area. Where the browser shrinks the layout viewport for the keyboard
       * itself, `dvh` already accounts for it and is the tighter of the two; where
       * it doesn't, `dvh` is the full height and the measurement wins. Taking the
       * smaller can only keep the footer on screen.
       */
      height: `min(100dvh, ${viewportHeight}px)`,
    }),
  }

  return (
    <FocusedShell style={shellStyle}>
      <HeyNicTopNav inputRef={inputRef} />
      <FocusedBody>
        <ChatHistorySidebar />
        <FocusedContent>
          <HeyNic inputRef={inputRef} />
        </FocusedContent>
      </FocusedBody>
    </FocusedShell>
  )
}

export default HeyNicChat
